import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cancelOrder, getPortfolio } from '../api/trading'
import { ApiError } from '../api/http'
import { useAuthStore } from '../auth/useAuthStore'
import { portfolioPollMs } from '../lib/marketHours'
import { PENDING_STATUSES } from '../hooks/usePortfolioSummary'
import { useSetting } from '../hooks/useSetting'
import { useMaskMoney } from '../privacy/usePrivacyStore'
import { toast } from '../toast/useToastStore'
import { underlyingOf } from '../lib/activeTrades'
import type { Order, PortfolioResponse } from '../api/types'

const ARM_TIMEOUT_MS = 3000

function rawFmtMoney(v: string | undefined): string {
  if (!v) return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return v
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message}: ${err.body || 'no detail'}`
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export const TickerPendingOrders = memo(function TickerPendingOrders({
  symbol,
}: {
  symbol: string
}) {
  const accountId = useAuthStore((s) => s.accountId)
  // Subscribe to the shared portfolio query but select only this symbol's
  // pending orders — structural sharing on the selected slice means the 5s
  // portfolio poll only re-renders this component when those orders actually
  // change, not on every position-value wiggle.
  const selectOrders = useCallback(
    (data: PortfolioResponse) =>
      data.orders.filter((o) => {
        if (!PENDING_STATUSES.test(o.status)) return false
        const sym = o.instrument.symbol
        if (o.instrument.type === 'OPTION') return underlyingOf(sym) === symbol
        return sym === symbol
      }),
    [symbol],
  )
  const { data: orders = [] } = useQuery({
    queryKey: ['portfolio', accountId],
    queryFn: getPortfolio,
    refetchInterval: portfolioPollMs,
    enabled: !!accountId,
    select: selectOrders,
    notifyOnChangeProps: ['data'],
  })
  const qc = useQueryClient()
  const mask = useMaskMoney()
  const fmtMoney = (v: string | undefined) => mask(rawFmtMoney(v))
  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)

  const [armedOrderId, setArmedOrderId] = useState<string | null>(null)
  const armTimerRef = useRef<number | null>(null)
  const [submittingOrderId, setSubmittingOrderId] = useState<string | null>(null)

  function clearArm() {
    if (armTimerRef.current != null) {
      window.clearTimeout(armTimerRef.current)
      armTimerRef.current = null
    }
    setArmedOrderId(null)
  }

  useEffect(() => () => clearArm(), [])

  const cancelMutation = useMutation({
    mutationFn: (orderId: string) => cancelOrder(orderId),
    // Optimistically drop the order from the cached portfolio so the row
    // disappears immediately; restored on error.
    onMutate: async (orderId) => {
      setSubmittingOrderId(orderId)
      const portfolioKey = ['portfolio', accountId]
      await qc.cancelQueries({ queryKey: portfolioKey })
      const previous = qc.getQueryData<PortfolioResponse>(portfolioKey)
      if (previous) {
        qc.setQueryData<PortfolioResponse>(portfolioKey, {
          ...previous,
          orders: previous.orders.filter((o) => o.orderId !== orderId),
        })
      }
      return { previous }
    },
    onSuccess: (_resp, orderId) => {
      setSubmittingOrderId(null)
      toast.success(`Order ${orderId} · Cancel submitted`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
    },
    onError: (err, orderId, ctx) => {
      setSubmittingOrderId(null)
      if (ctx?.previous) qc.setQueryData(['portfolio', accountId], ctx.previous)
      toast.error(`Cancel ${orderId} failed — ${errMessage(err)}`)
    },
  })

  function handleCancelClick(orderId: string) {
    if (submittingOrderId) return
    if (quickEnabled || armedOrderId === orderId) {
      clearArm()
      cancelMutation.mutate(orderId)
      return
    }
    setArmedOrderId(orderId)
    if (armTimerRef.current != null) window.clearTimeout(armTimerRef.current)
    armTimerRef.current = window.setTimeout(() => {
      setArmedOrderId(null)
      armTimerRef.current = null
    }, ARM_TIMEOUT_MS)
  }

  if (orders.length === 0) return null

  return (
    <div className="border-b border-zinc-800 bg-white/[0.02]">
      <div className="px-3 pt-2 text-[10px] uppercase tracking-wider text-zinc-500">
        Working · {orders.length}
      </div>
      <ul>
        {orders.map((o: Order) => {
          const isArmed = armedOrderId === o.orderId
          const isSubmitting = submittingOrderId === o.orderId
          const anySubmitting = submittingOrderId !== null
          const priceLabel =
            o.type === 'LIMIT'
              ? fmtMoney(o.limitPrice)
              : o.type === 'STOP'
                ? fmtMoney(o.stopPrice)
                : o.type === 'STOP_LIMIT'
                  ? `${fmtMoney(o.stopPrice)}/${fmtMoney(o.limitPrice)}`
                  : 'MKT'
          return (
            <li
              key={o.orderId}
              className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-800/60 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`text-[10px] font-medium ${
                      o.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {o.side}
                  </span>
                  <span className="font-mono text-xs text-zinc-100 truncate">
                    {o.instrument.symbol}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 font-mono truncate">
                  {o.type} · {o.quantity ?? '—'} @ {priceLabel}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleCancelClick(o.orderId)}
                disabled={anySubmitting}
                title={isArmed ? 'Click again to confirm' : 'Cancel order'}
                className={`flex-shrink-0 text-[11px] font-medium px-2.5 py-1.5 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  isArmed
                    ? 'bg-amber-500 text-white ring-1 ring-amber-300 animate-pulse'
                    : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'
                }`}
              >
                {isSubmitting ? '…' : isArmed ? 'Confirm' : 'Cancel'}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
})
